import { createAsyncThunk } from '@reduxjs/toolkit';
import { setTierInfo } from '../slices/tierSlice';
import { setUser } from '../slices/authSlice';
import { refreshAccessToken } from './authThunk';

// 'unresolved' most often means /tier/reconciled rejected a stale 15-min
// access token. Refresh once and retry so the tier recovers mid-session
// instead of staying frozen on the cached value until the next app start.
// Guarded so the refresh thunk's forced-logout path (no refresh token in
// Redux) can never fire from here.
const retryTierWithFreshToken = async (dispatch, getState, user) => {
    const { auth } = getState();
    const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;
    if (!isWeb && !auth.refreshToken) return null;
    await dispatch(refreshAccessToken());
    const token = getState().auth.accessToken
        || localStorage.getItem('auth:accessToken')
        || localStorage.getItem('web-shim:dev-access-token');
    if (!token) return null;
    const retried = await window.electron.tier.getInfo({ ...user, accessToken: token });
    return retried && !retried.unresolved && !retried.error ? retried : null;
};

export const initializeTier = createAsyncThunk(
    'tier/initialize',
    async (_, { dispatch, getState }) => {
        // Get current user from auth state
        const { auth } = getState();
        const user = auth.user;

        // Resolve access token — Redux state first, fall back to localStorage.
        // Electron uses 'auth:accessToken'; Capacitor shim uses 'web-shim:dev-access-token'.
        const accessToken = auth.accessToken || localStorage.getItem('auth:accessToken') || localStorage.getItem('web-shim:dev-access-token');

        // Fetch tier info — pass accessToken so the Electron handler can call
        // /api/tier/reconciled (which ORs Stripe + Apple/Google IAP).
        let tierInfo = await window.electron.tier.getInfo({ ...user, accessToken });
        // 'unresolved' = the server couldn't be reached/authenticated (stale
        // token after a cold resume, etc.). Do NOT apply it — that would
        // overwrite the hydrated/cached tier with 'free' and flash a paying
        // user back to free. Keep the current state; a later refresh resolves.
        if (tierInfo && (tierInfo.unresolved || tierInfo.error)) {
            const retried = await retryTierWithFreshToken(dispatch, getState, user);
            if (!retried) return tierInfo;
            tierInfo = retried;
        }
        dispatch(setTierInfo(tierInfo));

        // Identify user and set tier properties for analytics
        window.electron.analytics.identify(user.id || user._id, {
            email: user.email,
            name: user.name,
        });
        window.electron.analytics.setPersonProperties({
            tier: tierInfo.effectiveTier,
            tier_reason: tierInfo.reason,
            trial_start_date: tierInfo.trialStartDate || null,
            is_trial_expired: tierInfo.isTrialExpired || false,
        });

        return tierInfo;
    }
);

// After a successful IAP purchase/restore the client optimistically flips to
// GOAT, but the RevenueCat webhook reaches our server ~1-3s later. A single
// immediate refreshTier() races that webhook and gets back 'free', flashing the
// user back to free until a later refresh — the "showed free for a while then
// fixed itself" UX. This polls /reconciled and ONLY applies a result once it
// confirms GOAT; it never downgrades during the window (the local RC purchase
// already confirmed entitlement). If the server never confirms in the window we
// leave the optimistic GOAT in place — the next natural refresh reconciles.
export const confirmTierAfterPurchase = createAsyncThunk(
    'tier/confirmAfterPurchase',
    async (_, { dispatch, getState }) => {
        const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;
        const ATTEMPTS = 8;
        const DELAY_MS = 1500;
        for (let i = 0; i < ATTEMPTS; i++) {
            const { auth } = getState();
            let user = auth.user;
            const accessToken = auth.accessToken;
            if (isWeb || accessToken) {
                try {
                    const me = await window.electron.auth.getProfile(accessToken);
                    if (me && !me.error && me.id) { user = me; dispatch(setUser(me)); }
                } catch { /* keep cached user */ }
            }
            const pollToken = auth.accessToken || localStorage.getItem('auth:accessToken') || localStorage.getItem('web-shim:dev-access-token');
            try {
                const tierInfo = await window.electron.tier.getInfo({ ...user, accessToken: pollToken });
                if (tierInfo && tierInfo.effectiveTier === 'goat') {
                    dispatch(setTierInfo(tierInfo));
                    window.electron.analytics.setPersonProperties({
                        tier: tierInfo.effectiveTier,
                        tier_reason: tierInfo.reason,
                    });
                    return tierInfo;
                }
            } catch { /* transient — retry */ }
            await new Promise((r) => setTimeout(r, DELAY_MS));
        }
        // Not confirmed in time — keep the optimistic GOAT; a later refresh
        // (app refocus / next launch) reconciles once the webhook has landed.
        return null;
    }
);

export const refreshTier = createAsyncThunk(
    'tier/refresh',
    async (_, { dispatch, getState }) => {
        const { auth } = getState();
        let user = auth.user;

        // Pull a fresh user from the server so a cancelled subscription or
        // expired trial propagates to the client. If the access token is
        // expired or the network is down we fall back to the cached user —
        // the existing expiry logic in tierService/tierCache handles trials.
        // On web, the access token lives in an HttpOnly cookie — pass undefined
        // and let credentials:'include' carry it.
        const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;
        const accessToken = auth.accessToken;
        if (isWeb || accessToken) {
            try {
                const me = await window.electron.auth.getProfile(accessToken);
                if (me && !me.error && me.id) {
                    user = me;
                    dispatch(setUser(me));
                }
            } catch { /* transient — keep cached user */ }
        }

        const currentToken = auth.accessToken || localStorage.getItem('auth:accessToken') || localStorage.getItem('web-shim:dev-access-token');
        let tierInfo = await window.electron.tier.getInfo({ ...user, accessToken: currentToken });
        // Don't let an unresolved/failed fetch downgrade a cached GOAT to free
        // (the resume flash). Keep current state; the next refresh reconciles.
        if (tierInfo && (tierInfo.unresolved || tierInfo.error)) {
            const retried = await retryTierWithFreshToken(dispatch, getState, user);
            if (!retried) return tierInfo;
            tierInfo = retried;
        }
        dispatch(setTierInfo(tierInfo));

        // Sync tier properties on refresh
        window.electron.analytics.setPersonProperties({
            tier: tierInfo.effectiveTier,
            tier_reason: tierInfo.reason,
            trial_start_date: tierInfo.trialStartDate || null,
            is_trial_expired: tierInfo.isTrialExpired || false,
        });

        return tierInfo;
    }
);
